import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../../utils/hooks/use-async-state-callback";

import type { UUID } from "../../../utils/uuid";

/**
 * Moves a program or folder into a program, or to the top level with no parent, with a title, and
 * says if it failed. Resolves to whether it moved. Each contributor keeps their access to it. Only an
 * admin can move one.
 */
export function useMoveProgram(): {
  move: (id: UUID, title: string, parentId: UUID | null) => Promise<boolean>;
  moving: boolean;
} {
  const { load, loading: moving } = useAsyncStateSupabaseCallback(
    async ({ supabase }, id: UUID, title: string, parentId: UUID | null): Promise<boolean> => {
      const { error } = await supabase.rpc("move_program", {
        program_id_: id,
        title_: title,
        ...(parentId ? { parent_id_: parentId } : {}),
      });
      if (error) {
        toast.error(
          error.code === "23505"
            ? "Something there has that name already. Choose another name."
            : "Something went wrong moving the program. Please try again.",
        );
        return false;
      }
      return true;
    },
    [],
  );

  return { move: async (...args) => (await load(...args)) ?? false, moving };
}
